'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignUpForm from './SignUpForm';
import AuthBenefitsPanel from './AuthBenefitsPanel';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

export default function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left Benefits Panel — hidden on mobile */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] flex-shrink-0">
        <AuthBenefitsPanel />
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col min-h-screen bg-card">
        {/* Top Nav */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-extrabold text-lg text-primary hidden sm:block">TravelHub</span>
          </Link>
          <Link
            href="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeftIcon" size={14} />
            Back to search
          </Link>
        </div>

        {/* Form Area */}
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-md">
            {/* Mode Toggle */}
            <div className="flex items-center bg-muted rounded-xl p-1 mb-8">
              <button
                type="button"
                onClick={() => setMode('login')}
                className={`flex-1 py-2.5 text-sm font-700 rounded-lg transition-all duration-200 ${
                  mode === 'login' ?'bg-card text-primary shadow-sm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => setMode('signup')}
                className={`flex-1 py-2.5 text-sm font-700 rounded-lg transition-all duration-200 ${
                  mode === 'signup' ?'bg-card text-primary shadow-sm' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Create Account
              </button>
            </div>

            {/* Heading */}
            <div className="mb-6">
              <h1 className="text-2xl font-700 text-foreground">
                {mode === 'login' ? 'Welcome back' : 'Start saving on travel'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {mode === 'login' ?'Sign in to access your saved trips and price alerts.' :'Create your free account to save wishlists and get deal alerts.'}
              </p>
            </div>

            {/* Social Login */}
            <button
              type="button"
              className="w-full flex items-center justify-center gap-3 border border-border bg-card hover:bg-secondary rounded-xl py-3 text-sm font-semibold text-foreground transition-all duration-150 mb-4"
            >
              {/* Google SVG icon inline */}
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.96l3.007 2.332C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground font-medium">or continue with email</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Form */}
            {mode === 'login' ? (
              <LoginForm onSwitchToSignup={() => setMode('signup')} />
            ) : (
              <SignUpForm onSwitchToLogin={() => setMode('login')} />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border text-center">
          <p className="text-xs text-muted-foreground">
            By continuing, you agree to TravelHub's{' '}
            <Link href="/" className="text-primary hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link href="/" className="text-primary hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}